import type { EphemerisEntry, EphemerisResult } from "../components/types.js"
import { decToDegrees, raToDegrees } from "./coordinates.js"

/**
 * Horizons Support API `id` values for small-bodies (kind="SB") are NOT directly usable as
 * Horizons ephemeris `COMMAND`. The Horizons web app wraps them using the DES selector.
 */
export function commandFromSupportId(kind: unknown, id: unknown): string {
  const idStr = String(id ?? "").trim()
  if (!idStr) return idStr
  return kind === "SB" ? `DES=${idStr};NOFRAG;` : idStr
}

export function getHorizonsStepSize(interval: string): string {
  switch (interval) {
    case "5m":
      return "5 m"
    case "15m":
      return "15 m"
    case "1h":
      return "1 h"
    case "1d":
      return "1 d"
    default:
      return "1 h"
  }
}

export function formatDateTimeForHorizonsUtc(date: Date): string {
  // Format: "YYYY-MM-DD HH:MM:SS"
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, "0")
  const day = String(date.getUTCDate()).padStart(2, "0")
  const hours = String(date.getUTCHours()).padStart(2, "0")
  const minutes = String(date.getUTCMinutes()).padStart(2, "0")
  const seconds = String(date.getUTCSeconds()).padStart(2, "0")
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

export function computeStopTimeUtc(
  startDate: string,
  startTime: string,
  interval: string
): string {
  const start = new Date(`${startDate}T${startTime}:00`)

  let minutesPerStep: number
  switch (interval) {
    case "5m":
      minutesPerStep = 5
      break
    case "15m":
      minutesPerStep = 15
      break
    case "1h":
      minutesPerStep = 60
      break
    case "1d":
      minutesPerStep = 1440
      break
    default:
      minutesPerStep = 60
  }

  // 25 rows means 24 intervals (start + 24 steps)
  const totalMinutes = minutesPerStep * 24
  const end = new Date(start.getTime() + totalMinutes * 60000)
  return formatDateTimeForHorizonsUtc(end)
}

export function parseHorizonsResult(result: string): EphemerisResult {
  const lines = result.split("\n")
  const start = lines.findIndex(line => line.includes("$$SOE"))
  const end = lines.findIndex(line => line.includes("$$EOE"))
  if (start === -1 || end === -1 || end <= start) {
    throw new Error("Could not locate ephemeris table in response.")
  }

  const targetName = parseTargetName(lines, start)
  const headerLine = findCsvHeaderLine(lines, start)
  const headers = headerLine ? parseCsvRow(headerLine) : null
  const headerIndex = headers ? indexCsvHeaders(headers) : null

  const entries: EphemerisEntry[] = []
  for (let i = start + 1; i < end; i++) {
    const raw = lines[i].trim()
    if (!raw) continue
    if (!raw.includes(",")) continue
    const entry = parseCsvEphemerisRow(raw, headerIndex)
    if (entry) entries.push(entry)
  }
  return { entries, targetName }
}

function parseTargetName(lines: string[], soeIndex: number): string | null {
  for (let i = 0; i < soeIndex; i++) {
    const line = lines[i]
    const match = line.match(/Target body name:\s*(.+?)\s*\{/)
    if (match) return match[1].trim()
  }
  return null
}

function findCsvHeaderLine(lines: string[], soeIndex: number): string | null {
  for (let i = soeIndex - 1; i >= 0 && i >= soeIndex - 40; i--) {
    const line = lines[i].trim()
    if (!line) continue
    if (!line.includes(",")) continue
    const norm = line.toLowerCase()
    if (norm.includes("date") && (norm.includes("ut") || norm.includes("hr")))
      return line
    if (norm.includes("r.a") || norm.includes("ra") || norm.includes("dec"))
      return line
  }
  return null
}

function indexCsvHeaders(headers: string[]): Map<string, number> {
  const map = new Map<string, number>()
  headers.forEach((h, idx) => {
    map.set(normalizeHeader(h), idx)
  })
  return map
}

function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[\s_\.]+/g, "")
    .replace(/[^\w%]+/g, "")
}

function pickColumnIndex(
  headerIndex: Map<string, number>,
  candidates: string[]
): number | undefined {
  for (const c of candidates) {
    const idx = headerIndex.get(c)
    if (idx !== undefined) return idx
  }
  for (const c of candidates) {
    for (const [key, idx] of headerIndex.entries()) {
      if (key.includes(c)) return idx
    }
  }
  return undefined
}

function parseCsvEphemerisRow(
  line: string,
  headerIndex: Map<string, number> | null
): EphemerisEntry | null {
  const fields = parseCsvRow(line).map(f => f.trim())
  if (!fields.length) return null

  const idxTime = headerIndex
    ? pickColumnIndex(headerIndex, ["dateuthrmn", "dateut", "date"])
    : undefined
  const idxRa = headerIndex
    ? pickColumnIndex(headerIndex, ["raicrf", "ra"])
    : undefined
  const idxDec = headerIndex
    ? pickColumnIndex(headerIndex, ["decicrf", "dec"])
    : undefined
  const idxAzi = headerIndex
    ? pickColumnIndex(headerIndex, ["azi", "azimuth"])
    : undefined
  const idxElev = headerIndex
    ? pickColumnIndex(headerIndex, ["elev", "elv", "elevation"])
    : undefined
  const idxTom = headerIndex
    ? pickColumnIndex(headerIndex, ["tom", "t-o-m", "to-m"])
    : undefined
  const idxMnIllu = headerIndex
    ? pickColumnIndex(headerIndex, ["mnillu%", "mn_illu%", "mnillu", "illu%"])
    : undefined

  const hasHeader = !!headerIndex && headerIndex.size > 0
  let timeField: string | undefined
  let raField: string | undefined
  let decField: string | undefined
  let aziField: string | undefined
  let elevField: string | undefined
  let tomField: string | undefined
  let mnIlluField: string | undefined

  if (hasHeader && idxRa !== undefined && idxDec !== undefined) {
    timeField = idxTime !== undefined ? fields[idxTime] : fields[0] ?? undefined
    if (
      timeField &&
      !timeField.includes(":") &&
      idxTime !== undefined &&
      fields[idxTime + 1] &&
      /^\d{2}:\d{2}/.test(fields[idxTime + 1])
    ) {
      timeField = `${timeField} ${fields[idxTime + 1]}`
    }
    raField = fields[idxRa]
    decField = fields[idxDec]
    aziField = idxAzi !== undefined ? fields[idxAzi] : fields[3]
    elevField = idxElev !== undefined ? fields[idxElev] : fields[4]
    tomField = idxTom !== undefined ? fields[idxTom] : undefined
    mnIlluField = idxMnIllu !== undefined ? fields[idxMnIllu] : undefined
  } else {
    // Fallback parsing for QUANTITIES='1,4,25'
    if (fields[1] && /^\d{2}:\d{2}/.test(fields[1])) {
      timeField = `${fields[0]} ${fields[1]}`
      raField = fields[2]
      decField = fields[3]
      aziField = fields[4]
      elevField = fields[5]
      tomField = fields.length > 6 ? fields[6] : undefined
      mnIlluField = fields.length > 7 ? fields[7] : undefined
    } else {
      timeField = fields[0]
      raField = fields[1]
      decField = fields[2]
      aziField = fields[3]
      elevField = fields[4]
      tomField = fields.length > 5 ? fields[5] : undefined
      mnIlluField = fields.length > 6 ? fields[6] : undefined
    }
  }

  if (!timeField || !raField || !decField || !aziField || !elevField)
    return null

  // Twilight and moon markers are in separate CSV fields, not embedded in time field.
  // When Horizons uses QUANTITIES='1,4,25', the CSV format is:
  // Date/Time, TwilightMarker, MoonMarker, RA, DEC, Azi, Elev, T-O-M, MN_Illu%
  // Twilight markers: '*'=day, 'C'=civil, 'N'=nautical, 'A'=astronomical, ' '=night
  const twilightMarkerField =
    idxTime !== undefined ? fields[idxTime + 1] : fields[1]
  const moonMarkerField =
    idxTime !== undefined ? fields[idxTime + 2] : fields[2]
  const twilightMarker = twilightMarkerField?.trim() || ""
  const moonPresent = moonMarkerField?.trim() === "m"

  const ra = raField.replace(/\s+/g, " ").trim()
  const dec = decField.replace(/\s+/g, " ").trim()
  const azi = Number.parseFloat(aziField)
  const elev = Number.parseFloat(elevField)
  if (!Number.isFinite(azi) || !Number.isFinite(elev)) return null

  const raDeg = raToDegrees(ra)
  const decDeg = decToDegrees(dec)

  const moonSeparation = tomField ? Number.parseFloat(tomField) : undefined
  const moonIllumination = mnIlluField
    ? Number.parseFloat(mnIlluField)
    : undefined

  return {
    time: timeField,
    ra,
    dec,
    raDeg,
    decDeg,
    azi,
    elev,
    moonSeparation:
      moonSeparation !== undefined && Number.isFinite(moonSeparation)
        ? moonSeparation
        : undefined,
    moonIllumination:
      moonIllumination !== undefined && Number.isFinite(moonIllumination)
        ? moonIllumination
        : undefined,
    twilight: twilightMarker,
    moonPresent,
  }
}

/**
 * Minimal CSV row parser supporting quoted fields.
 */
function parseCsvRow(line: string): string[] {
  const out: string[] = []
  let cur = ""
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      const next = line[i + 1]
      if (inQuotes && next === '"') {
        cur += '"'
        i++
        continue
      }
      inQuotes = !inQuotes
      continue
    }
    if (ch === "," && !inQuotes) {
      out.push(cur)
      cur = ""
      continue
    }
    cur += ch
  }
  out.push(cur)
  return out
}
